import { openCopilotOverlay } from '@/shared/stores/ui';

import type { ProposedAction } from '@/entities/proposed-actions';
import type { SourceProposedActionT } from '@/shared/lib';

/**
 * Mode the Prior Auth action wizard should open in.
 *
 * This is derived from the proposed-action ``tool_name`` when the clinician
 * executes an action from the plan, or set directly when the tab invokes the
 * wizard on-demand (e.g. "Upload attachment" from a case row).
 */
export type PriorAuthActionMode =
  | 'answer_question'
  | 'upload_attachment'
  | 'submit'
  | 'cancel'
  | 'appeal'
  | 'deny';

/**
 * Payload for the Prior Auth action wizard.
 *
 * ``action`` is optional because the tab can launch the wizard without a
 * proposed action — in that case we fall back to ``mode``/``recordId``/``caseId``
 * to know what to do.
 *
 * ``patientUserId`` is optional because proposed-action executions don't carry
 * it — the wizard fetches details directly by ``recordId`` and doesn't need
 * the patient context.
 */
export interface PriorAuthActionPrefill {
  mode: PriorAuthActionMode;
  recordId: string;
  caseId: number;
  patientUserId?: string;
  dosespotPrescriptionId?: string;
  pendingQuestionId?: number | null;
  action?: ProposedAction;
  sourceProposedAction?: SourceProposedActionT;
}

declare module '@/shared/stores/ui/copilotOverlay' {
  interface CopilotOverlayPayloads {
    priorAuthAction: PriorAuthActionPrefill;
  }
}

/**
 * Open the Prior Auth action wizard with its payload.
 */
export function openPriorAuthActionOverlay(prefill: PriorAuthActionPrefill): void {
  openCopilotOverlay('priorAuthAction', prefill);
}
